import { CourseApi, courseschedulesApi } from "api";
import { useToaster } from "utils";
import { PaziaModal } from "components/molecules";
import { deFormatDate, toTimeFormat } from "utils";
import { CourseCreateActionTypes, useCourseCreate } from "../../CourseCreateContext";
import { BaseScheduleFormSchema } from "./BaseScheduleForm";
import { ScheduleTopicFormSchema } from "./ScheduleTopicForm";
import { ScheduleTopicGroupFormSchema } from "./ScheduleTopicGroupForm";
import { ScheduleTypeSelectionForm } from "./ScheduleTypeSelectionForm";
import { ScheduleFormTrainers } from "../BaseTrainersScheduleForm";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { useScheduleConflictConfirm } from "../conflicts/useScheduleConflictConfirm";

interface CreateNewEventModalProps {
  onCloseRequest: () => void;
  isOpen?: boolean;
  courseId: number;
  course: CourseApi;
}

export const CreateNewEventModal = ({ onCloseRequest, isOpen, courseId, course }: CreateNewEventModalProps) => {
  const { showError, showSuccess } = useToaster();
  const { dispatch } = useCourseCreate();
  const { confirmSave, dialog: conflictDialog } = useScheduleConflictConfirm(Number(courseId));

  const onBreakSubmit = async (data: BaseScheduleFormSchema) => {
    try {
      const request = {
        day: deFormatDate(data.day)!,
        timeFrom: toTimeFormat(+data.fromTime),
        timeTo: toTimeFormat(+data.toTime),
        courseSessionId: Number(data.session),
      };
      await courseschedulesApi.createBreak(Number(courseId), request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
    } catch (e) {
      console.error(e);
      showError("Nie udało się dodać przerwy.");
    } finally {
      onCloseRequest();
    }
  };
  const onTopicSubmit = async (data: ScheduleTopicFormSchema) => {
    const request = {
      day: deFormatDate(data.day)!,
      timeFrom: toTimeFormat(+data.fromTime),
      timeTo: toTimeFormat(+data.toTime),
      courseSessionId: Number(data.session),
      placeId: Number(data.place),
      placeType: Number(data.placeType),
      topicId: Number(data.topic),
      trainers: ScheduleFormTrainers.toRequestTrainers(data.trainers),
    };
    // Warn (not block) when the trainer or the room is already booked at that time
    if (!(await confirmSave({ ...request, trainerIds: request.trainers.map((t) => t.trainerId) }))) return false;
    try {
      await courseschedulesApi.createTopic(Number(courseId), request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
    } catch (e) {
      console.error(e);
      showError("Nie udało się dodać tematu.");
    } finally {
      onCloseRequest();
    }
  };

  const onTopicGroupSubmit = async (data: ScheduleTopicGroupFormSchema) => {
    const request = {
      day: deFormatDate(data.day)!,
      timeFrom: toTimeFormat(+data.fromTime),
      timeTo: toTimeFormat(+data.toTime),
      courseSessionId: Number(data.session),
      placeId: Number(data.place),
      placeType: Number(data.placeType),
      topicGroupId: Number(data.topicGroup),
      trainers: ScheduleFormTrainers.toRequestTrainers(data.trainers),
    };
    if (!(await confirmSave({ ...request, trainerIds: request.trainers.map((t) => t.trainerId) }))) return false;
    try {
      await courseschedulesApi.createTopicGroup(Number(courseId), request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
      showSuccess("Zajęcia zostały dodane do harmonogramu.");
    } catch (e) {
      console.error(e);
      showError("Nie udało się dodać grupy tematów.");
    } finally {
      onCloseRequest();
    }
  };

  return (
    <PaziaModal
      id="edit-session-modal"
      onCloseRequest={onCloseRequest}
      isOpen={!!isOpen}
      size="7xl"
      className="h-[90dvh] p-4"
    >
      <ModalPadding>
        <ModalHeader>Dodaj nowe wydarzenie</ModalHeader>
        <ScheduleTypeSelectionForm
          formStyle={{ height: "calc(90dvh - 13rem)" }}
          formClassName="overflow-auto"
          course={course}
          onBreakSubmit={onBreakSubmit}
          onTopicSubmit={onTopicSubmit}
          onTopicGroupSubmit={onTopicGroupSubmit}
        />
      </ModalPadding>
      {conflictDialog}
    </PaziaModal>
  );
};
